'use strict';

/**
 * MarkupOverlay — SVG layer that renders the markups for the current page
 * on top of the PDF canvas. Coordinates come in as PDF points; we use the
 * pdf.js viewport.convertToViewportPoint to project them to screen pixels.
 *
 * Receives `widthPx`, `heightPx`, `viewport` as props injected by PdfCanvas
 * via React.cloneElement.
 *
 * Props:
 *   markups          full markups[] array (we filter to current page)
 *   pageNumber       1-based current page
 *   selectedIdx      currently selected markup index (-1 if none)
 *   onSelectMarkup   (idx) => void
 *   onUpdateMarkup   (idx, patch) => void — for drag handles
 */

import React, { useState, useRef, useCallback, useMemo } from 'react';

// Label pill metrics (single-line, no truncation, small font so most
// construction-review labels fit on one line).
const PILL_H = 16;
const PILL_GAP = 3;
const PILL_OFFSET_X = 14;   // distance from balloon to label start
const PILL_FONT_PX = 9;
const PILL_CHAR_W = 5.0;    // approx px/char for 9px sans
const PILL_PAD_X = 7;

const RISK_COLOR = {
  low: '#0EA5A4',     // teal
  medium: '#F59E0B',  // amber
  high: '#DC2626',    // red
};

function colorOf(m) {
  return RISK_COLOR[m.risk] || RISK_COLOR.medium;
}

// Our markups are stored in fitz (PyMuPDF) convention: coords measured
// from the VISUAL top-left of the rendered page, x→right, y→down. fitz's
// page.rect always presents this normalized view regardless of the
// underlying MediaBox origin OR any /Rotate the PDF declares.
//
// pdf.js's viewport.scale is the multiplier from this same visual-pixel
// space (at scale=1) to current rendered canvas pixels. So the
// conversion is just a scalar multiply — works for centered viewBoxes,
// rotated pages, and standard pages without special-casing any of them.
//
// Bug history:
//   v1 used viewBox[3] - fitzY for the y-flip — broke on centered
//      viewBoxes (places balloons above the page).
//   v2 used viewBox[0] + fitzX / viewBox[3] - fitzY (matched fitz origin
//      but called convertToViewportPoint which is rotation-naive about
//      our fitz-style input) — broke on rotated pages (coords swapped,
//      pushed markups off-canvas).
//   v3 (this) multiplies by viewport.scale directly. Rotation-independent.

function pdfToScreen(viewport, x, fitzY) {
  if (!viewport) return [0, 0];
  return [x * viewport.scale, fitzY * viewport.scale];
}

function screenToPdf(viewport, x, y) {
  if (!viewport) return [0, 0];
  return [x / viewport.scale, y / viewport.scale];
}

export default function MarkupOverlay({
  markups = [],
  pageNumber = 1,
  selectedIdx = -1,
  onSelectMarkup,
  onUpdateMarkup,
  // injected by PdfCanvas:
  widthPx,
  heightPx,
  viewport,
  displayZoom = 1,  // visual zoom applied via CSS transform on the parent;
                    // SVG bounding rect comes back scaled by it, so we
                    // divide screen-pixel deltas by displayZoom before
                    // running them through PDF.js's coord math.
}) {
  const svgRef = useRef(null);
  // Drag state. Three drag modes:
  //   - point drag:  handle is a string ('from'|'to'|'tl'|'br') or number
  //                  (cloud-point index). Moves that single endpoint to
  //                  wherever the cursor is.
  //   - body drag:   handle === 'body'. Translates the entire markup
  //                  (every coord/from/to/rect/etc) by the delta between
  //                  cursor and `startPdf`. `startMarkup` snapshots the
  //                  shape at drag start so we re-apply delta to the
  //                  original each move (no drift).
  const [drag, setDrag] = useState(null);

  const onMouseDownHandle = useCallback((idx, handle) => (evt) => {
    evt.stopPropagation();
    evt.preventDefault();
    setDrag({ idx, handle });
  }, []);

  // Body-drag mousedown: selects the markup AND arms a translate-all
  // gesture in one go. Threshold of 4px prevents single clicks from
  // hijacking themselves into micro-translations.
  const onMouseDownBody = useCallback((idx) => (evt) => {
    if (evt.button !== 0) return;  // left button only
    evt.stopPropagation();
    onSelectMarkup?.(idx);
    const svg = svgRef.current;
    if (!svg || !viewport) return;
    const rect = svg.getBoundingClientRect();
    const screenToPdfNow = (clientX, clientY) =>
      screenToPdf(viewport, (clientX - rect.left) / displayZoom, (clientY - rect.top) / displayZoom);
    const startPdf = screenToPdfNow(evt.clientX, evt.clientY);
    // Deep-snapshot the markup at drag start so we recompute from the
    // original coords each move (avoids cumulative floating-point drift).
    const startMarkup = JSON.parse(JSON.stringify(markups[idx]));
    setDrag({ idx, handle: 'body', startPdf, startMarkup });
  }, [markups, onSelectMarkup, viewport, displayZoom]);

  const onMouseMove = useCallback(
    (evt) => {
      if (!drag || !viewport) return;
      const svg = svgRef.current;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      // rect is in VISUAL CSS pixels (CSS-transform aware), so the cursor
      // delta is display-zoomed. Divide by displayZoom to recover the
      // SVG's own (pre-transform) coord space before handing off to PDF.js.
      const x = (evt.clientX - rect.left) / displayZoom;
      const y = (evt.clientY - rect.top) / displayZoom;
      const [pdfX, pdfY] = screenToPdf(viewport, x, y);
      const m = markups[drag.idx];
      if (!m) return;

      // Body drag: translate every coord/from/to/rect by delta from
      // drag-start cursor position, applied to the snapshot.
      if (drag.handle === 'body' && drag.startPdf && drag.startMarkup) {
        const dx = pdfX - drag.startPdf[0];
        const dy = pdfY - drag.startPdf[1];
        const s = drag.startMarkup;
        if (s.type === 'callout' && Array.isArray(s.from) && Array.isArray(s.to)) {
          onUpdateMarkup?.(drag.idx, {
            from: [s.from[0] + dx, s.from[1] + dy],
            to:   [s.to[0]   + dx, s.to[1]   + dy],
          });
        } else if (s.type === 'scope_box' && Array.isArray(s.rect) && s.rect.length === 4) {
          onUpdateMarkup?.(drag.idx, {
            rect: [s.rect[0] + dx, s.rect[1] + dy, s.rect[2] + dx, s.rect[3] + dy],
          });
        } else if (s.type === 'cloud' && Array.isArray(s.coords)) {
          onUpdateMarkup?.(drag.idx, {
            coords: s.coords.map((p) => [p[0] + dx, p[1] + dy]),
          });
        }
        return;
      }

      // Point drag: just snap the one endpoint to the cursor.
      if (m.type === 'callout') {
        if (drag.handle === 'from') onUpdateMarkup?.(drag.idx, { from: [pdfX, pdfY] });
        else if (drag.handle === 'to') onUpdateMarkup?.(drag.idx, { to: [pdfX, pdfY] });
      } else if (m.type === 'scope_box') {
        const [x0, y0, x1, y1] = m.rect;
        if (drag.handle === 'tl') onUpdateMarkup?.(drag.idx, { rect: [pdfX, pdfY, x1, y1] });
        else if (drag.handle === 'br') onUpdateMarkup?.(drag.idx, { rect: [x0, y0, pdfX, pdfY] });
      } else if (m.type === 'cloud' && typeof drag.handle === 'number') {
        const next = m.coords.map((p, i) => (i === drag.handle ? [pdfX, pdfY] : p));
        onUpdateMarkup?.(drag.idx, { coords: next });
      }
    },
    [drag, viewport, markups, onUpdateMarkup, displayZoom]
  );

  // -------- Label layout pass (collision avoidance) --------
  // Pre-compute each markup's preferred balloon anchor + pill rect, then
  // walk top-down and shift overlapping pills downward (or flip to the
  // left if a pill would run off the right edge). Single 9px line per
  // label — no wrap, no truncation — and the balloon stays at its
  // original anchor; only the pill moves. A thin connector line bridges
  // balloon to pill when they aren't adjacent anymore.
  const labelLayouts = useMemo(() => {
    if (!viewport || !widthPx) return [];
    const anchors = markups.map((m, idx) => {
      if (Number(m.page) !== Number(pageNumber)) return null;
      const t = m?.type;
      let bx = 0;
      let by = 0;
      if (t === 'cloud' && Array.isArray(m.coords) && m.coords.length >= 3) {
        const [x, y] = pdfToScreen(viewport, m.coords[0][0], m.coords[0][1]);
        bx = x - 12; by = y - 12;
      } else if (t === 'callout' && Array.isArray(m.from) && Array.isArray(m.to)) {
        const [tx, ty] = pdfToScreen(viewport, m.to[0], m.to[1]);
        bx = tx + 6; by = ty - 12;
      } else if (t === 'scope_box' && Array.isArray(m.rect) && m.rect.length === 4) {
        const [x0, y0] = pdfToScreen(viewport, m.rect[0], m.rect[1]);
        bx = x0 + 6; by = y0 + 6;
      } else if (typeof m?.anchor === 'string') {
        const ma = /\[\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\]/.exec(m.anchor);
        if (!ma) return null;
        const [px, py] = pdfToScreen(viewport, Number(ma[1]), Number(ma[2]));
        bx = px; by = py;
      } else { return null; }
      const label = (m.label || '').replace(/\s+/g, ' ').trim();
      const pillW = Math.max(40, Math.min(label.length, 200) * PILL_CHAR_W + PILL_PAD_X * 2);
      return { idx, bx, by, pillW, color: colorOf(m), label };
    }).filter(Boolean);

    // Pack pills top-down by initial balloon Y. We track placed pill
    // rects and push subsequent overlaps below them.
    const sorted = anchors.slice().sort((a, b) => a.by - b.by);
    const placed = [];
    const overlaps = (a, b) =>
      a.pillX < b.pillX + b.pillW &&
      a.pillX + a.pillW > b.pillX &&
      a.pillY < b.pillY + PILL_H &&
      a.pillY + PILL_H > b.pillY;
    const result = [];
    for (const a of sorted) {
      // Default to the right of the balloon, vertically centered on it.
      let pillX = a.bx + PILL_OFFSET_X;
      let pillY = a.by - PILL_H / 2;
      // If the pill would exceed the canvas on the right, flip to the left.
      if (pillX + a.pillW > widthPx - 4) {
        pillX = a.bx - PILL_OFFSET_X - a.pillW;
      }
      // Push down for any collision with previously placed pills.
      let safety = 0;
      while (safety++ < 50) {
        const c = placed.find((p) => overlaps({ pillX, pillY, pillW: a.pillW }, p));
        if (!c) break;
        pillY = c.pillY + PILL_H + PILL_GAP;
      }
      result.push({ idx: a.idx, bx: a.bx, by: a.by, pillX, pillY, pillW: a.pillW, color: a.color, label: a.label });
      placed.push({ pillX, pillY, pillW: a.pillW });
    }
    // Return in original markup-index order for fast lookup in render.
    const byIdx = new Array(markups.length).fill(null);
    for (const r of result) byIdx[r.idx] = r;
    return byIdx;
  }, [markups, pageNumber, viewport, widthPx]);

  const onMouseUp = useCallback(() => {
    setDrag(null);
  }, []);

  const onSurfaceClick = useCallback(() => {
    // Click on empty area inside the overlay → deselect
    if (!drag) onSelectMarkup?.(-1);
  }, [drag, onSelectMarkup]);

  if (!widthPx || !heightPx || !viewport) return null;

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 pointer-events-auto"
      width={widthPx}
      height={heightPx}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseUp}
      onClick={onSurfaceClick}
      style={{ pointerEvents: 'none' }}
    >
      {markups.map((m, idx) => {
        const layout = labelLayouts[idx];
        // Lenient page compare — older sessions stored page as a string in
        // JSONB; new sessions always store numbers.
        if (Number(m.page) !== Number(pageNumber)) return null;
        const color = colorOf(m);

        // Recovery path for legacy sessions where the agent stored only
        // metadata (not coords/from/to/rect). annotate_drawing.py emits
        // `anchor` as a HUMAN string like:
        //   "region [100,200]-[300,400]"   (cloud)
        //   "arrow to [400,500]"           (callout)
        //   "box [100,200]-[300,400]"      (scope_box)
        // We parse the first [x,y] pair as a balloon anchor so the user can
        // at least see WHERE the markup belongs even without full geometry.
        // Re-running the agent (with the joboticsAgent.js fix) restores
        // proper shapes.
        const hasGeometry =
          (m.type === 'cloud' && Array.isArray(m.coords)) ||
          (m.type === 'callout' && Array.isArray(m.from) && Array.isArray(m.to)) ||
          (m.type === 'scope_box' && Array.isArray(m.rect));
        if (!hasGeometry) {
          let anchorXY = null;
          if (Array.isArray(m.anchor) && m.anchor.length === 2) {
            anchorXY = [Number(m.anchor[0]), Number(m.anchor[1])];
          } else if (typeof m.anchor === 'string') {
            // Match the FIRST [x,y] pair in the string.
            const ma = /\[\s*([-\d.]+)\s*,\s*([-\d.]+)\s*\]/.exec(m.anchor);
            if (ma) anchorXY = [Number(ma[1]), Number(ma[2])];
          }
          if (anchorXY && Number.isFinite(anchorXY[0]) && Number.isFinite(anchorXY[1])) {
            const [bx, by] = pdfToScreen(viewport, anchorXY[0], anchorXY[1]);
            // High-contrast balloon so it pops over dense black-ink drawings.
            return (
              <g key={idx} style={{ pointerEvents: 'visiblePainted' }}>
                <circle cx={bx} cy={by} r={24} fill="#fff" fillOpacity={0.9} stroke={color} strokeWidth={2} />
                <circle cx={bx} cy={by} r={20} fill={color} fillOpacity={0.25} stroke={color} strokeWidth={2} strokeDasharray="4 3" />
                <circle cx={bx} cy={by} r={13} fill={color} stroke="#fff" strokeWidth={2} onClick={(evt) => { evt.stopPropagation(); onSelectMarkup?.(idx); }} />
                <text x={bx} y={by + 5} textAnchor="middle" fontSize={13} fontWeight="800" fill="#fff" style={{ userSelect: 'none', pointerEvents: 'none' }}>
                  {idx + 1}
                </text>
              </g>
            );
          }
        }
        const selected = idx === selectedIdx;
        const strokeWidth = selected ? 3 : 2;
        const opacity = selected ? 1 : 0.85;
        const onClick = (evt) => {
          evt.stopPropagation();
          onSelectMarkup?.(idx);
        };

        if (m.type === 'cloud' && Array.isArray(m.coords) && m.coords.length >= 3) {
          const pts = m.coords.map(([x, y]) => pdfToScreen(viewport, x, y));
          const pointsStr = pts.map((p) => `${p[0]},${p[1]}`).join(' ');
          // Balloon center: top-left point + offset
          const [bx, by] = pts[0];
          return (
            <g key={idx} style={{ pointerEvents: 'visiblePainted' }}>
              <polygon
                points={pointsStr}
                fill={color}
                fillOpacity={0.06}
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray="6 4"
                opacity={opacity}
                style={{ cursor: 'move' }}
                onClick={onClick}
                onMouseDown={onMouseDownBody(idx)}
              />
              <BalloonBadge x={bx - 12} y={by - 12} number={idx + 1} label={m.label} selected={selected} color={color} onClick={onClick} layout={layout} />
              {selected && pts.map((p, i) => (
                <DragHandle
                  key={`c-${idx}-${i}`}
                  x={p[0]}
                  y={p[1]}
                  color={color}
                  onMouseDown={onMouseDownHandle(idx, i)}
                />
              ))}
            </g>
          );
        }

        if (m.type === 'callout' && Array.isArray(m.from) && Array.isArray(m.to)) {
          const [fx, fy] = pdfToScreen(viewport, m.from[0], m.from[1]);
          const [tx, ty] = pdfToScreen(viewport, m.to[0], m.to[1]);
          return (
            <g key={idx} style={{ pointerEvents: 'visiblePainted' }}>
              <line
                x1={fx}
                y1={fy}
                x2={tx}
                y2={ty}
                stroke={color}
                strokeWidth={strokeWidth + 8}  /* invisible wide hit area for easier grabbing */
                strokeOpacity={0}
                strokeLinecap="round"
                style={{ cursor: 'move' }}
                onMouseDown={onMouseDownBody(idx)}
                onClick={onClick}
              />
              <line
                x1={fx}
                y1={fy}
                x2={tx}
                y2={ty}
                stroke={color}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                opacity={opacity}
                markerEnd={`url(#arrow-${color.slice(1)})`}
                style={{ pointerEvents: 'none' }}
              />
              <BalloonBadge x={tx + 6} y={ty - 12} number={idx + 1} label={m.label} selected={selected} color={color} onClick={onClick} layout={layout} />
              {selected && (
                <>
                  <DragHandle x={fx} y={fy} color={color} onMouseDown={onMouseDownHandle(idx, 'from')} />
                  <DragHandle x={tx} y={ty} color={color} onMouseDown={onMouseDownHandle(idx, 'to')} />
                </>
              )}
              <defs>
                <marker
                  id={`arrow-${color.slice(1)}`}
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 0 L 10 5 L 0 10 z" fill={color} />
                </marker>
              </defs>
            </g>
          );
        }

        if (m.type === 'scope_box' && Array.isArray(m.rect) && m.rect.length === 4) {
          const [x0, y0, x1, y1] = m.rect;
          const [sx0, sy0] = pdfToScreen(viewport, x0, y0);
          const [sx1, sy1] = pdfToScreen(viewport, x1, y1);
          const left = Math.min(sx0, sx1);
          const top = Math.min(sy0, sy1);
          const w = Math.abs(sx1 - sx0);
          const h = Math.abs(sy1 - sy0);
          return (
            <g key={idx} style={{ pointerEvents: 'visiblePainted' }}>
              <rect
                x={left}
                y={top}
                width={w}
                height={h}
                fill={color}
                fillOpacity={0.05}
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray="8 5"
                opacity={opacity}
                style={{ cursor: 'move' }}
                onClick={onClick}
                onMouseDown={onMouseDownBody(idx)}
              />
              <BalloonBadge x={left + 6} y={top + 6} number={idx + 1} label={m.label} selected={selected} color={color} onClick={onClick} layout={layout} />
              {selected && (
                <>
                  <DragHandle x={sx0} y={sy0} color={color} onMouseDown={onMouseDownHandle(idx, 'tl')} />
                  <DragHandle x={sx1} y={sy1} color={color} onMouseDown={onMouseDownHandle(idx, 'br')} />
                </>
              )}
            </g>
          );
        }

        return null;
      })}
    </svg>
  );
}

// Numbered balloon + sticky-note label.
// The pill position is precomputed by the collision-avoidance pass in
// MarkupOverlay so labels never overlap each other. When the pill has
// been shifted away from the balloon to clear a collision, a thin
// connector line bridges them so the user can still tell which label
// belongs to which balloon (Word / Adobe review-pane style).
function BalloonBadge({ x, y, number, label, color, selected, onClick, layout }) {
  const text = (label || '').replace(/\s+/g, ' ').trim();
  // Use the precomputed layout when available; otherwise fall back to a
  // simple to-the-right placement so the component still works in tests
  // / paths that don't run the collision pass.
  const pillW = layout?.pillW ?? Math.max(40, text.length * PILL_CHAR_W + PILL_PAD_X * 2);
  const pillX = layout?.pillX ?? (x + PILL_OFFSET_X);
  const pillY = layout?.pillY ?? (y - PILL_H / 2);
  // Draw a connector when the pill has been displaced (collision shift)
  // or flipped to the left of the balloon.
  const pillMidY = pillY + PILL_H / 2;
  const pillLeft = pillX;
  const pillRight = pillX + pillW;
  const onRight = pillLeft >= x;
  const connectorTargetX = onRight ? pillLeft : pillRight;
  const showConnector = Math.abs(pillMidY - y) > 2 || (onRight ? pillLeft - x > PILL_OFFSET_X + 2 : x - pillRight > PILL_OFFSET_X + 2);
  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      {showConnector ? (
        <line
          x1={x}
          y1={y}
          x2={connectorTargetX}
          y2={pillMidY}
          stroke={color}
          strokeWidth={1}
          strokeDasharray="2 2"
          opacity={0.7}
          pointerEvents="none"
        />
      ) : null}
      {text ? (
        <g>
          <rect
            x={pillX}
            y={pillY}
            width={pillW}
            height={PILL_H}
            rx={3}
            fill="#ffffff"
            fillOpacity={selected ? 1 : 0.95}
            stroke={color}
            strokeWidth={selected ? 1.5 : 1}
          />
          <text
            x={pillX + PILL_PAD_X}
            y={pillY + PILL_H / 2 + PILL_FONT_PX / 2 - 1}
            fontSize={PILL_FONT_PX}
            fontWeight={500}
            fill="#1e293b"
            style={{ userSelect: 'none' }}
          >
            {text}
          </text>
        </g>
      ) : null}
      <circle cx={x} cy={y} r={11} fill={color} stroke="#fff" strokeWidth={1.5} />
      <text
        x={x}
        y={y + 4}
        textAnchor="middle"
        fontSize={11}
        fontWeight="700"
        fill="#fff"
        style={{ userSelect: 'none' }}
      >
        {number}
      </text>
    </g>
  );
}

function DragHandle({ x, y, color, onMouseDown }) {
  return (
    <rect
      x={x - 5}
      y={y - 5}
      width={10}
      height={10}
      fill="#fff"
      stroke={color}
      strokeWidth={2}
      style={{ cursor: 'move', pointerEvents: 'visiblePainted' }}
      onMouseDown={onMouseDown}
    />
  );
}
